import { useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useStore } from "src/stores/Store";
import tw from "src/utils/tw";
import { AddOfferingRoute } from "types/offerings";
import BackButton from "../buttons/BackButton";
import { BorderButton } from "../buttons/BorderButton";
import CancelButton from "../buttons/CancelButton";
import Container from "../layout/Container";

interface Props {
  route: AddOfferingRoute;
  routes: AddOfferingRoute[];
  onCancel: () => void;
  onNext: () => void;
  lastStep?: boolean;
  noNextButton?: boolean;
  isEdit?: boolean;
  toggleSettings?: () => void;
  visited?: string[];
  setVisited?: (visited) => void;
  disabled?: boolean;
}

export default function AddOfferingHeader(props: Props) {
  const store = useStore();
  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    if (props.visited) {
      const newVisited = [...props.visited];
      if (!newVisited.includes(props.route.path)) {
        newVisited.push(props.route.path);
      }
      props.setVisited(newVisited);
    }
  }, []);

  let currentIndex = props.routes?.findIndex(
    (r) => r.path === location.pathname
  );

  if (currentIndex < 0) {
    currentIndex = 0;
  }

  function onClickStep(i) {
    const step = props.routes[i];
    history.push(`${step.path}${location.search}`);
  }

  function renderSteps() {
    const steps = props.routes.map((r, i) => {
      const isPreviousStep = i < currentIndex;
      const isCurrentStep = i === currentIndex;
      const isVisitedStep =
        props.visited.includes(r.path) && !isCurrentStep && !isPreviousStep;

      return (
        <button
          key={i}
          className={tw(
            "flex-1",
            "px-1",
            "flex",
            "bg-errors",
            "pt-1",
            "pb-4",
            isPreviousStep || isVisitedStep ? "cursor-pointer" : "cursor-auto"
          )}
          css={[
            {
              "&:hover": {
                "& .popover": {
                  opacity: 1,
                  transform: "translateY(0)",
                },
              },
            },
            isPreviousStep && {
              "&:hover": {
                "& .step-button": {
                  background: store.theme.orgColor,
                },
              },
            },
          ]}
          onClick={
            isPreviousStep || isVisitedStep ? () => onClickStep(i) : undefined
          }
        >
          <div
            className={tw(
              isCurrentStep && "bg-organization",
              isPreviousStep && "bg-dark",
              isVisitedStep && "bg-dark",
              !isPreviousStep &&
                !isCurrentStep &&
                !isVisitedStep &&
                "bg-borderGray",
              "transition",
              "h-1.5",
              "rounded-full",
              "transition",
              "w-full",
              "step-button"
            )}
          >
            {(isPreviousStep || isVisitedStep) && (
              <div className="popover -translate-y-4 transform opacity-0 transition-all">
                <svg
                  className="absolute left-0 top-2 z-10 h-2 w-full"
                  x="0px"
                  y="0px"
                  viewBox="0 0 255 255"
                >
                  <polygon fill="#fff" points="0,127.5 127.5,0 255,127.5" />
                </svg>
                <div
                  css={{ boxShadow: `0 -1px 8px rgba(0,0,0,.2)` }}
                  className={tw(
                    "w-24",
                    "absolute",
                    "py-2",
                    "px-2",
                    "text-center",
                    "bg-white",
                    "left-0",
                    "right-0",
                    "mx-auto",
                    "top-3",
                    "-mt-px",
                    "rounded"
                  )}
                >
                  <div className="block text-xs font-bold leading-tight">
                    {r.title}
                  </div>
                </div>
              </div>
            )}
          </div>
        </button>
      );
    });

    return <div className="-mx-1 flex">{steps}</div>;
  }

  if (props.isEdit) {
    return (
      <>
        <div className="relative z-10 -mb-3">
          <h1 className="px-12 pt-8 sm:hidden">{props.route.title}</h1>

          <div className="hidden pt-4 sm:block">
            <BorderButton
              className="rounded-l-none bg-lightGray pl-4 pr-8"
              onClick={props.toggleSettings}
            >
              <i
                aria-hidden
                className="far fa-ellipsis pr-1 text-organization"
              />{" "}
              {props.route.title}
            </BorderButton>
          </div>
        </div>
      </>
    );
  }

  return (
    <div
      className={tw(
        "h-32",
        "border-b",
        "border-borderGray",
        "fixed",
        "top-0",
        "left-0",
        "w-full",
        "bg-white",
        "z-30",
        store.user.isSalesOrAbove ? "pt-11" : "pt-4"
      )}
      // css={{ zIndex: 4 }}
    >
      <Container large>
        <div className="mb-2 flex items-center">
          <div className="flex-1">
            {currentIndex === 0 && <CancelButton onClick={props.onCancel} />}
            {currentIndex !== 0 && <BackButton />}

            <h2 className="mb-0">{props.route.title}</h2>
          </div>

          {!props.noNextButton && (
            <div>
              <BorderButton
                onClick={props.onNext}
                type="submit"
                disabled={props.disabled ?? false}
              >
                {props.lastStep ? "Finish" : "Next"}
              </BorderButton>
            </div>
          )}
        </div>

        {renderSteps()}
      </Container>
    </div>
  );
}
